import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Schema } from 'zod';

import { CustomFormProvider } from '@/shared/components/ui/form/context/form-context';
import { StyledForm } from '@/shared/components/ui/form/form.styled';
import { TFormProps } from '@/shared/components/ui/form/form.types';
import { LoaderModal } from '@/shared/components/ui/loader-modal/loader-modal';
import { filterDirtyFields } from '@/shared/utils/form';
import { stripTypename } from '@/shared/utils/strip-typename';

const Form = <TFormValues extends FieldValues>({
  onSubmit,
  children,
  className,
  id,
  schema,
  defaultValues,
  loaderText,
  onSubmitted,
}: TFormProps<TFormValues, Schema>) => {
  const [currentSchema, setCurrentSchema] = useState<Schema>(schema);
  const { t } = useTranslation();
  const sanitizedDefaultValues = useMemo(
    () => stripTypename(defaultValues),
    [defaultValues],
  );
  const methods = useForm<TFormValues>({
    resolver: zodResolver(currentSchema),
    mode: 'all',
    defaultValues: sanitizedDefaultValues,
  });

  useEffect(() => {
    if (!methods.formState.isSubmitSuccessful) {
      return;
    }
    methods.reset(sanitizedDefaultValues);
  }, [methods.formState.isSubmitSuccessful]);

  const handleSubmit = async (data: TFormValues) => {
    const sanitizedData = stripTypename(data);
    const dirtyFields = methods.formState.dirtyFields;
    const dirtyData = stripTypename(
      filterDirtyFields<TFormValues>(sanitizedData, dirtyFields),
    );
    await onSubmit({ data: sanitizedData, dirtyData }, methods);
  };

  return (
    <CustomFormProvider
      schema={schema}
      currentSchema={currentSchema}
      setCurrentSchema={setCurrentSchema}
    >
      <FormProvider {...methods}>
        <StyledForm
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            methods.handleSubmit(handleSubmit)(event);
            onSubmitted?.();
          }}
          className={className}
          id={id}
        >
          <>
            <LoaderModal
              headline={t('ui:modal.headline')}
              open={methods.formState.isSubmitting}
              loaderText={loaderText ?? ''}
            />
            {children(methods)}
          </>
        </StyledForm>
      </FormProvider>
    </CustomFormProvider>
  );
};

export { Form };
